/**
 * Pickup Slot Settings Page
 * READ-ONLY view of core's platform slot configuration (S1–S5 windows,
 * cutoffs, labels, active flags) — slot changes are managed in core, not here
 * (view-only per Ali 2026-07-27). The pickup closed-day / holiday calendar
 * below remains editable and is relayed to core's trusted endpoints.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCog, CalendarOff, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, Badge } from '../components/ui';
import * as pickupSlotsApi from '../services/pickupSlots.api';
import type { ClosedDay } from '../services/pickupSlots.api';
import { useCan } from '../contexts/useAuth';

export default function SlotSettings() {
  const can = useCan();
  const queryClient = useQueryClient();
  const [newClosedOn, setNewClosedOn] = useState('');
  const [newReason, setNewReason] = useState('');
  const [closedDayError, setClosedDayError] = useState<string | null>(null);

  const configQuery = useQuery({
    queryKey: ['slotConfig'],
    queryFn: () => pickupSlotsApi.getSlotConfig(),
  });
  const closedDaysQuery = useQuery({
    queryKey: ['closedDays'],
    queryFn: () => pickupSlotsApi.getClosedDays(),
  });

  const addClosedDayMutation = useMutation({
    mutationFn: () => pickupSlotsApi.addClosedDay(newClosedOn, newReason.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['closedDays'] });
      setNewClosedOn('');
      setNewReason('');
      setClosedDayError(null);
    },
    onError: (error: { response?: { data?: { error?: string } } }) => {
      setClosedDayError(error.response?.data?.error || 'Failed to add closed day');
    },
  });

  const removeClosedDayMutation = useMutation({
    mutationFn: (date: string) => pickupSlotsApi.removeClosedDay(date),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['closedDays'] }),
    onError: (error: { response?: { data?: { error?: string } } }) => {
      setClosedDayError(error.response?.data?.error || 'Failed to remove closed day');
    },
  });

  const handleRemoveClosedDay = (day: ClosedDay) => {
    if (window.confirm(`Reopen ${day.closed_on}${day.reason ? ` (${day.reason})` : ''} for pickups?`)) {
      removeClosedDayMutation.mutate(day.closed_on);
    }
  };

  const meta = configQuery.data?.meta;

  return (
    <div className="p-6 space-y-8">
      {/* ---- Slot configuration (view-only) ---- */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarCog className="w-6 h-6 text-blue-600" />
              Pickup Slot Settings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Platform slot windows &amp; cutoffs (Asia/Baghdad) — view only. Slot configuration is
              managed in core.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {meta && (
              <span className="text-xs text-gray-500">
                source: {meta.source}
                {meta.fetched_at ? ` · fetched ${new Date(meta.fetched_at).toLocaleTimeString()}` : ''}
              </span>
            )}
            <button
              onClick={() => configQuery.refetch()}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {configQuery.isLoading && <p className="text-gray-500">Loading slot configuration…</p>}
        {configQuery.isError && <p className="text-red-600">Failed to load slot configuration. Try refreshing.</p>}

        {configQuery.data && (
          <Card padding="none">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase">
                    <th className="px-4 py-3">Slot</th>
                    <th className="px-4 py-3">Label (EN)</th>
                    <th className="px-4 py-3">Label (AR)</th>
                    <th className="px-4 py-3">Window</th>
                    <th className="px-4 py-3">Cutoff (min before end)</th>
                    <th className="px-4 py-3">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {configQuery.data.slots.map((slot) => (
                    <tr key={slot.code} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-900">{slot.code}</td>
                      <td className="px-4 py-3 text-gray-700">{slot.labels?.en ?? slot.title}</td>
                      <td className="px-4 py-3 text-gray-700" dir="rtl">
                        {slot.labels?.ar ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {slot.window.from} – {slot.window.to}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{slot.cutoff_minutes_before_end}</td>
                      <td className="px-4 py-3">
                        <Badge variant={slot.active ? 'success' : 'default'}>
                          {slot.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ---- Closed days ---- */}
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarOff className="w-5 h-5 text-amber-600" />
            Pickup Closed Days
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Holidays / non-operational dates — core rolls any pickup landing on these days to the next open day.
          </p>
        </div>

        {closedDayError && (
          <div className="mb-3 px-4 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
            {closedDayError}
          </div>
        )}

        <div className="flex items-end gap-3 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date</label>
            <input
              type="date"
              value={newClosedOn}
              onChange={(e) => setNewClosedOn(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex-1 max-w-sm">
            <label className="block text-xs text-gray-500 mb-1">Reason (optional)</label>
            <input
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="e.g. Eid al-Adha"
              maxLength={255}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          {can('slots.close') && (
            <button
              onClick={() => {
                setClosedDayError(null);
                addClosedDayMutation.mutate();
              }}
              disabled={!newClosedOn || addClosedDayMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {addClosedDayMutation.isPending ? 'Adding…' : 'Add closed day'}
            </button>
          )}
        </div>

        {closedDaysQuery.isLoading && <p className="text-gray-500">Loading closed days…</p>}
        {closedDaysQuery.isError && <p className="text-red-600">Failed to load closed days. Try refreshing.</p>}

        {closedDaysQuery.data && (
          <Card padding="none">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...closedDaysQuery.data]
                    .sort((a, b) => a.closed_on.localeCompare(b.closed_on))
                    .map((day) => (
                      <tr key={day.closed_on} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{day.closed_on}</td>
                        <td className="px-4 py-3 text-gray-700">{day.reason || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            {can('slots.close') && (
                              <button
                                onClick={() => handleRemoveClosedDay(day)}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                                title="Reopen this date"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  {closedDaysQuery.data.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                        No closed days configured — every day is operational.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
